import React, {useEffect, useState} from "react";
import {Box, CircularProgress, Typography} from "@mui/material";
import {listShares, Share, ShareKind} from "../util/api";

/**
 * The folders, projects or documents shared with the person signed in -- one page per kind, the same list for each.
 *
 * NO SEARCH, NO BROWSING OUTSIDE WHAT WAS SHARED, as on every page here: there is no way to reach anything nobody
 * chose to share, and a search box would imply there is.
 */
const HEADING = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E0E0E0";

export const KIND_WORDS: Record<ShareKind, {title: string; lead: string; none: string}> = {
    Folder: {title: "Your folders", lead: "Folders shared with you, with the released records filed in them.",
             none: "No folders have been shared with you yet."},
    Project: {title: "Your projects", lead: "Projects shared with you, with their released records.",
              none: "No projects have been shared with you yet."},
    Document: {title: "ParkourAI Documents", lead: "Documents shared with you from ParkourAI Docs. Each opens in ParkourAI Docs.",
               none: "No documents have been shared with you yet."},
};

export function when(value?: string | null): string {
    if (!value) return "not recorded";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

export default function Shares({kind, onOpen, problem}: {kind: ShareKind; onOpen: (one: Share) => void; problem?: string | null}) {
    const [rows, setRows] = useState<Share[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const words = KIND_WORDS[kind];

    useEffect(() => {
        setLoading(true);
        listShares(kind).then(result => {
            if (result.status === 200) {
                setRows(result.data?.Results ?? []);
                setError(null);
            } else {
                setError(result.data?.Cause || result.data?.Code || "That list could not be loaded.");
            }
            setLoading(false);
        });
    }, [kind]);

    if (loading) {
        return <Box sx={{display: "flex", justifyContent: "center", py: 10}}><CircularProgress size={24}/></Box>;
    }

    return (
        <Box sx={{maxWidth: 900, mx: "auto", px: 3, py: 6, boxSizing: "border-box"}}>
            <Typography component="h1" sx={{fontSize: 24, fontWeight: 500, color: HEADING}}>{words.title}</Typography>
            <Typography sx={{fontSize: 14, color: MUTED, mt: 1, lineHeight: 1.6}}>{words.lead}</Typography>

            {error && <Typography sx={{fontSize: 14, color: "#B3261E", mt: 4}}>{error}</Typography>}
            {problem && <Typography sx={{fontSize: 14, color: "#B3261E", mt: 2}}>{problem}</Typography>}
            {!error && rows.length === 0 && (
                <Typography sx={{fontSize: 14, color: MUTED, mt: 4, lineHeight: 1.6}}>{words.none}</Typography>
            )}

            <Box sx={{mt: 4, display: "flex", flexDirection: "column", gap: 1.5}}>
                {rows.map(one => (
                    <Box key={one.USID} onClick={() => onOpen(one)}
                         sx={{border: `1px solid ${BORDER}`, borderRadius: 2, p: 2.5, cursor: "pointer",
                              backgroundColor: "#FFFFFF", "&:hover": {borderColor: "#1498D8"}}}>
                        <Typography sx={{fontSize: 16, fontWeight: 600, color: HEADING}}>
                            {one.Number ? `${one.Number} · ${one.Name}` : one.Name}
                        </Typography>
                        <Typography sx={{fontSize: 13, color: MUTED, mt: 0.5}}>
                            {one.From ? `From ${one.From}` : "From your supplier"} · shared {when(one.SharedDatetime)}
                            {one.ExpiresDatetime ? ` · until ${when(one.ExpiresDatetime)}` : ""}
                        </Typography>
                        {one.Note && <Typography sx={{fontSize: 13, color: HEADING, mt: 1}}>{one.Note}</Typography>}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
